import { useCallback, useEffect, useMemo, useState } from "react";
import { API_HOST } from "../constants";
import type { TelegramUser, LessonStatus } from "../types";

// Кеш для прогресса курсов
const progressCache: Record<string, { progress: Record<string, LessonStatus>; timestamp: number }> = {};
const progressFetchPromises: Record<string, Promise<Record<string, LessonStatus>>> = {};
const CACHE_DURATION = 30 * 1000; // 30 секунд

export const useCourseProgress = (user: TelegramUser | null, courseSlug: string) => {
  const [progress, setProgress] = useState<Record<string, LessonStatus>>({});
  const [loading, setLoading] = useState(true);

  // Загрузка прогресса из БД
  useEffect(() => {
    if (!user || !courseSlug) {
      setProgress({});
      setLoading(false);
      return;
    }

    let isMounted = true;
    const cacheKey = `${user.id}-${courseSlug}`;

    const fetchProgress = async () => {
      // Проверяем кеш
      const cached = progressCache[cacheKey];
      const now = Date.now();
      if (cached && (now - cached.timestamp) < CACHE_DURATION) {
        if (isMounted) {
          setProgress(cached.progress);
          setLoading(false);
        }
        return;
      }

      // Если уже есть запрос для этого ключа, ждем его
      if (progressFetchPromises[cacheKey]) {
        try {
          const cachedProgress = await progressFetchPromises[cacheKey];
          if (isMounted) {
            setProgress(cachedProgress);
            setLoading(false);
          }
          return;
        } catch {
          // Продолжаем с новым запросом
        }
      }

      try {
        if (isMounted) {
          setLoading(true);
        }

        // Создаем промис для загрузки прогресса
        const fetchPromise = (async () => {
          const response = await fetch(`${API_HOST}/api/user/${user.id}/course-progress/${courseSlug}`);
          
          if (!response.ok) {
            console.error("Failed to fetch course progress");
            return {};
          }

          const data = await response.json();
          const progressMap: Record<string, LessonStatus> = {};
          
          for (const [lessonId, progressData] of Object.entries(data)) {
            progressMap[lessonId] = (progressData as any).status as LessonStatus;
          }
          
          // Обновляем кеш
          progressCache[cacheKey] = { progress: progressMap, timestamp: Date.now() };
          
          return progressMap;
        })();

        // Сохраняем промис для предотвращения дублирующихся запросов
        progressFetchPromises[cacheKey] = fetchPromise;

        const progressMap = await fetchPromise;

        // Удаляем промис после завершения
        delete progressFetchPromises[cacheKey];

        if (isMounted) {
          setProgress(progressMap);
        }
      } catch (error) {
        delete progressFetchPromises[cacheKey];
        if (isMounted) {
          console.error("Error fetching course progress:", error);
          setProgress({});
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchProgress();

    return () => {
      isMounted = false;
    };
  }, [user, courseSlug]);

  const updateProgress = useCallback(
    async (lessonId: string, status: LessonStatus) => {
      if (!user || !courseSlug) return;

      const cacheKey = `${user.id}-${courseSlug}`;

      // Оптимистичное обновление
      const newProgress = { ...progress, [lessonId]: status };
      setProgress(newProgress);

      // Обновляем кеш
      if (progressCache[cacheKey]) {
        progressCache[cacheKey] = {
          progress: newProgress,
          timestamp: Date.now(),
        };
      }

      try {
        const response = await fetch(`${API_HOST}/api/user/${user.id}/course-progress`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            course_slug: courseSlug,
            lesson_id: lessonId,
            status: status,
          }),
        });

        if (!response.ok) {
          console.error("Failed to update course progress");
          // Откатываем оптимистичное обновление при ошибке
          // Но не откатываем, так как это может быть race condition
        }
      } catch (error) {
        console.error("Error updating course progress:", error);
      }
    },
    [user, courseSlug, progress]
  );

  const setLessonStatus = useCallback(
    (lessonId: string, status: LessonStatus) => {
      updateProgress(lessonId, status);
    },
    [updateProgress]
  );

  const stats = useMemo(() => {
    const total = Object.keys(progress).length;
    const done = Object.values(progress).filter((s) => s === "done").length;
    const inProgress = Object.values(progress).filter((s) => s === "in_progress").length;
    return { total, done, inProgress };
  }, [progress]);

  return {
    progress,
    setLessonStatus,
    stats,
    loading,
  };
};
